import {
  Badge,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SkeletonText,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import {
  FundraisingStatus,
  SubscriptionWorkflowType,
  useGetSubscriptionQuery,
  useUpdateSubscriptionInvestAmountMutation,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import {
  ErrorMessage,
  ErrorQueryCard,
  ResponsiveModal,
  ResponsiveModalFooter,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useInvestStatusBadge } from '@blockpulse3/ui/ui-hooks';

import { schema } from './schema';
import { SubscriptionDetailsInvestAmountForm } from './type';

type Props = {
  subscriptionId: string;
};

/**
 * SubscriptionDetailsInvestAmount.
 * Update invest amount section of the subscription side panel.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsInvestAmount({ subscriptionId }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');
  const i18nSubscriptionDetailsInvestAmount = useTranslations('SubscriptionDetailsInvestAmount');

  const { isOpen, onClose, onToggle } = useDisclosure();

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);

  const { register, formState, handleSubmit, reset } = useForm<SubscriptionDetailsInvestAmountForm>(
    {
      resolver: yupResolver(schema),
    },
  );

  const { data, loading, error, refetch } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const [updateSubscriptionInvestAmount] = useUpdateSubscriptionInvestAmountMutation();

  /* ** Invest status badge ** */
  const investStatus = useInvestStatusBadge();

  if (error) {
    return <ErrorQueryCard h="30px" mx="3" />;
  }

  const handleModalOpen = (): void => {
    reset({ amount: data?.subscription.investAmount });
    onToggle();
  };

  const handleFormSubmit: SubmitHandler<SubscriptionDetailsInvestAmountForm> = (data): void => {
    setIsUpdateLoading(true);
    updateSubscriptionInvestAmount({
      variables: {
        updateSubscriptionInvestAmountInput: {
          subscriptionId,
          amount: data.amount,
        },
      },
      onCompleted: (response) => {
        if (response.updateSubscriptionInvestAmount) {
          successToast({
            title: i18nSubscriptionDetailsInvestAmount('Tooltip.UpdateAmountSuccess'),
          });
          refetch();
          onClose();
        }
        setIsUpdateLoading(false);
      },
      onError: () => {
        errorToast({ title: i18nSubscriptionDetailsInvestAmount('Tooltip.UpdateAmountError') });
        setIsUpdateLoading(false);
      },
    });
  };

  const subscription = data?.subscription;
  const investStatusBadge = investStatus(subscription, subscription?.fundraising?.status);
  const amount = subscription?.investAmount
    ? formatNumberCurrency(subscription?.investAmount)
    : '-';
  const isEditAmountDisabled = subscription?.fundraising.status !== FundraisingStatus.CLOSED;
  const isAllInSubscriptionWorkflow =
    subscription?.fundraising.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;

  return (
    <Stack px="4" spacing="1">
      <HStack>
        <Text color="gray.600" fontWeight="600">
          {i18nSubscriberDetailsSidePanel('Investment')}
        </Text>
        <Badge colorScheme={investStatusBadge.color} fontSize="xs">
          {investStatusBadge.label}
        </Badge>
      </HStack>
      {loading ? (
        <SkeletonText noOfLines={1} skeletonHeight={8} width="50%" />
      ) : (
        <Stack spacing="1">
          <HStack alignItems="center" spacing="2">
            <Text fontSize="xl" fontWeight="700">
              {amount}
            </Text>
            {isAllInSubscriptionWorkflow && (
              <>
                <Tooltip hasArrow label="Modifier le montant" placement="top">
                  <IconButton
                    aria-label="edit amount"
                    icon={<Icon as={PencilIcon} />}
                    isDisabled={isEditAmountDisabled}
                    size="sm"
                    variant="secondary"
                    onClick={handleModalOpen}
                  />
                </Tooltip>
                <ResponsiveModal isOpen={isOpen} onClose={onClose}>
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>
                      {i18nSubscriptionDetailsInvestAmount('EditModalTitle')}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <form id="update-invest-amount" onSubmit={handleSubmit(handleFormSubmit)}>
                        <FormControl isInvalid={!!formState.errors.amount}>
                          <FormLabel htmlFor="investAmount">
                            {i18nSubscriptionDetailsInvestAmount('InvestAmountLabel')}
                          </FormLabel>
                          <InputGroup id="investAmount">
                            <Input step="0.01" type="number" {...register('amount')} />
                            <InputRightAddon>€</InputRightAddon>
                          </InputGroup>
                          <ErrorMessage error={formState.errors.amount} />
                        </FormControl>
                      </form>
                    </ModalBody>
                    <Divider />
                    <ResponsiveModalFooter>
                      <Button type="button" variant="secondary" onClick={onClose}>
                        {i18nGeneral('Cancel')}
                      </Button>
                      <Button
                        form="update-invest-amount"
                        isLoading={isUpdateLoading}
                        type="submit"
                        variant="primary"
                      >
                        {i18nGeneral('Submit')}
                      </Button>
                    </ResponsiveModalFooter>
                  </ModalContent>
                </ResponsiveModal>
              </>
            )}
          </HStack>
          {(subscription?.minimalAmount || subscription?.maximalAmount) && (
            <HStack color="gray.500" fontSize="xs">
              {subscription?.minimalAmount && (
                <>
                  <Text fontWeight="600">{i18nSubscriptionDetailsInvestAmount('MinAmount')}</Text>
                  <Text>{formatNumberCurrency(subscription.minimalAmount)}</Text>
                  {subscription.maximalAmount && <Text>-</Text>}
                </>
              )}
              {subscription?.maximalAmount && (
                <>
                  <Text fontWeight="600">{i18nSubscriptionDetailsInvestAmount('MaxAmount')}</Text>
                  <Text>{formatNumberCurrency(subscription.maximalAmount)}</Text>
                </>
              )}
            </HStack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export type SubscriptionDetailsInvestAmountProps = Props;
